import React, {useState, useEffect, useContext} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import ShortFiler from '../../../../../types/filers/shortFiler';
import { Avatar, Button, Checkbox,CircularProgress,debounce, FormControlLabel, Grid2 as Grid, InputLabel, Link, List, ListItem, ListItemAvatar, ListItemText, MenuItem, Paper, Select, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@mui/material';
import { Search } from '@mui/icons-material';
import { blue} from '@mui/material/colors';

import '../../../view-filers.css';
import { FilersDataContext } from '../../../Filers';

interface Props {
  isFullTable: boolean;
  onClose?: () => void;
}

const FilerListTable = (props:Props): JSX.Element => {
  
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoading, setIsLoading] = useState(false);

  const defaultTabs = ['Summary', 'Reports', 'Contributions', 'Expenditures', 'Correspondence', 'Audit']
  let defaultTabsCutOffIndex = 3;

  const [filerListing, setFilerListing] = useState<ShortFiler[]>([]);
  const FilerListingContext = useContext(FilersDataContext);
  const { showBoundary } = useErrorBoundary();

  const [searchInput, setSearchInput] = useState('');
  const [displayGroup, setDisplayGroup] = useState('C');
  const [isInitialLoad, setIsInitialLoad] = useState(true); 
  const [excludeClosed, setExclueClosed] = useState(true);
  const [loadMore, setLoadMore] = useState(false);

  useEffect(() => {
    if(FilerListingContext.filerListing.length>0 && isInitialLoad){
      setFilerListing(FilerListingContext.filerListing);
      setIsInitialLoad(false);
    }
    else{
      refreshFilerList();
    }
  }, [searchInput, displayGroup, excludeClosed, loadMore]);


  const getCurrentFiler = async (cpfId: number) => {
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/filer/payload/${cpfId}`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get filer data.'));
        }
        return result.json();
      })
      .then(result => {
        FilerListingContext.setCurrentFilerPayload(result);
        FilerListingContext.setContentType('Summary');
      });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
    
  }

  const getCurrentFilerDocuments = async (cpfId: number) => {
    try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/filer/correspondence/all/${cpfId}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get filer documents.'));
          }
          return result.json();
        })
        .then(result => {
            FilerListingContext.setCurrentFilerDocuments(result.documents);
            FilerListingContext.setCurrentFilerAuditIssues(result.issues);
        });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const getCurrentFilerRelatedReports = async (cpfId: number) => {
    try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/related/${cpfId}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get filer related reports.'));
          }
          return result.json();
        })
        .then(result => {
            if(result.length > 0){
              defaultTabs.push('Related Reports');
              defaultTabsCutOffIndex = 4;
            }
            FilerListingContext.setCurrentFilerRelatedReports(result);
            FilerListingContext.setContentTypes(defaultTabs);
            FilerListingContext.setTypesCutOffIndex(defaultTabsCutOffIndex);
        });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const updateCurrentFiler = async (cpfId: number) => {
    getCurrentFiler(cpfId);
    getCurrentFilerDocuments(cpfId);
    getCurrentFilerRelatedReports(cpfId);
    
    if(!props.isFullTable){
      props.onClose && props.onClose();
    }

    updateLinkParams(cpfId);
  }

  const updateSearchPhrase = debounce((event: any) => {
          let searchInput = event.target.value.toLowerCase();
          if(searchInput.length === 0){
            setDisplayGroup('C');
            setSearchInput("");
            setExclueClosed(true);
          }
          else{
            setDisplayGroup('A');
            setSearchInput(searchInput);
            setExclueClosed(false);
          }
        }, 800);

  const updateDisplayGroup = (event: any) => {
    setDisplayGroup(event.target.value);
  }

  const showClosed = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExclueClosed(!event.target.checked);
  };

 const refreshFilerList = async () => {
    try{

      let encodedSearchInput = encodeURIComponent(searchInput);
      let url = searchInput?`${process.env.REACT_APP_API_BASE_URL}/filers/listings/A?searchPhrase=${encodedSearchInput}&excludeClosed=${excludeClosed}`
      :`${process.env.REACT_APP_API_BASE_URL}/filers/listings/${displayGroup}?first200=${!loadMore}&excludeClosed=${excludeClosed}`;

      await fetch(url)
      .then(result => result.json())
      .then(result => {
        setIsLoading(false);
        setFilerListing(result);
        FilerListingContext.setFilerListing(result);
        setIsInitialLoad(false);
        if(result.length && searchInput.length>0 && !props.isFullTable){
          let firstFiler = result[0];
          let currentFilerCpfId = firstFiler? firstFiler.cpfId : 0;
          getCurrentFiler(currentFilerCpfId);
          getCurrentFilerDocuments(currentFilerCpfId);
          getCurrentFilerRelatedReports(currentFilerCpfId);
          updateLinkParams(currentFilerCpfId);
        }
    });
    }catch(error){
      console.error('Uncaught Error:', error);
    }
 }

 const updateLinkParams = (cpfId: number) => {
    //update the url
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('q', cpfId.toString());
  
    // Update the URL in the browser's history without reloading
    window.history.pushState({}, '', `${url.pathname}?${params.toString()}`);
 }

 const loadMoreFilerList = () => {
    setLoadMore(true);
    setIsLoading(true);
 }

  return (
    <Box padding={2} display={'flex'} flexDirection={'column'} height={1}>
      <Grid container>
        <Grid size={{ xs: 12, md: 10 }}>
          <Stack direction={props.isFullTable?"row": "column"} spacing={1}>
            <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id='filer-type-label'>Type</InputLabel>
                <Select 
                  labelId="filer-type-label"
                  id='filer-type'
                  aria-labelledby='filer-type-label'
                  label="Group"
                  value={displayGroup}
                  onChange={updateDisplayGroup}
                >
                  <MenuItem dense value={'A'}>All Registered Filers</MenuItem>
                  <MenuItem dense value={'C'} sx={{fontWeight:550}}>All Candidates</MenuItem>
                  <MenuItem dense value={'SW'} sx={{textIndent:5}}> Statewide</MenuItem>
                  <MenuItem dense value={'U'} sx={{textIndent:5}}> Legislative</MenuItem>
                  <MenuItem dense value={'SE'} sx={{textIndent:10}}> - Senate</MenuItem>
                  <MenuItem dense value={'HO'} sx={{textIndent:10}}>- House</MenuItem>
                  <MenuItem dense value={'M'} sx={{textIndent:5}}>Mayoral</MenuItem>
                  <MenuItem dense value={'CC'} sx={{textIndent:5}}>City Council</MenuItem>
                  <MenuItem dense value={'GC'} sx={{textIndent:5}}>Governor's Council</MenuItem>
                  <MenuItem dense value={'CJ'} sx={{textIndent:5}}>County & Judicial</MenuItem>
                  <MenuItem dense value={'S'} sx={{textIndent:5}}>Barnstable Assembly</MenuItem>
                  <MenuItem dense value={'AP'} sx={{fontWeight:550}}>All PACs</MenuItem>
                  <MenuItem dense value={'P'} sx={{textIndent:5}}>Traditional PACs</MenuItem>
                  <MenuItem dense value={'I'} sx={{textIndent:5}}>IE PACs</MenuItem>
                  <MenuItem dense value={'Y'} sx={{textIndent:5}}>People's Committees</MenuItem>
                  <MenuItem dense value={'E'} sx={{fontWeight:550}}>State Parties</MenuItem>
                  <MenuItem dense value={'L'} sx={{fontWeight:550}}>Local Party Committees</MenuItem>
                  <MenuItem dense value={'DL'} sx={{textIndent:5}}>Democratic</MenuItem>
                  <MenuItem dense value={'RL'} sx={{textIndent:5}}>Republican</MenuItem>
                  <MenuItem dense value={'OL'} sx={{textIndent:5}}>Other Party</MenuItem>
                  <MenuItem dense value={'X'} sx={{fontWeight:550}}>Ballot Question Committees</MenuItem>
                  <MenuItem dense value={'B'} sx={{fontWeight:550}}>Banks</MenuItem>
                  <MenuItem dense value={'V'} sx={{fontWeight:550}}>Segregated Funds</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }} variant="outlined" size="small">
              <OutlinedInput
                aria-label='search by filer name'
                title='search by filer name'
                placeholder={"Filter by name"}
                onChange={updateSearchPhrase}
                startAdornment={
                <InputAdornment position="start">
                <Search />
                </InputAdornment>
              }
            />
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{xs:12, md:props.isFullTable?2:10}}>
          <Stack direction="row">
            <FormControl size='small'>
              <FormControlLabel control={<Checkbox checked={!excludeClosed} onChange={showClosed} inputProps={{ 'aria-label': 'show closed' }} />} label="show inactive filers" sx={{fontSize: "smaller"}} />
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={12} marginTop={2} marginBottom={10}>
          <TableContainer component={Paper}>
          <Table aria-label="filer list table">
          {
            props.isFullTable &&
            <TableHead sx={{ bgcolor: 'alternate.main' }}>
              <TableRow>
              <TableCell>
                <Typography
                  variant={'subtitle2'}
                  fontWeight={550}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Name
                </Typography>
              </TableCell>
              {
                !isSM &&
                <TableCell>
                  <Typography
                    variant={'subtitle2'}
                    fontWeight={550}
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Type
                  </Typography>
                </TableCell>
              }
              <TableCell>
                <Typography
                  variant={'subtitle2'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Office Sought
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant={'subtitle2'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Status
                </Typography>
              </TableCell>
              </TableRow>
            </TableHead>
          }
          <TableBody>
            {filerListing.map((filer: ShortFiler) => (
              <React.Fragment key={filer.cpfId}>
                {
                  props.isFullTable &&
                  <TableRow
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 }, 
                      "&:hover": {
                      boxShadow: "0 0 10px #ccc",
                    },
                    }}
                    onClick={() => { window.location.href = `/filers?q=${filer.cpfId}`; }}
                    className="is-clickable"
                    >
                    <TableCell>
                      <List sx={{ p: 0, m: 0 }}>
                        <ListItem 
                          sx={{ p: 0, m: 0 }} 
                          >
                            {
                              filer.isCandidate && 
                              <ListItemAvatar>
                                <Avatar alt={filer.fullName + ' image'} src={filer.filerThumbnailPhotoUrl} />
                              </ListItemAvatar>
                            }
                            <ListItemText
                              primary={`${filer.cpfId} ${filer.filerNameReverse}`}
                              primaryTypographyProps ={{
                                variant: 'subtitle2',
                                fontWeight: 550,
                                color: filer.isActive? 'primary': 'error',
                              }}
                            />
                          </ListItem>
                      </List>
                    </TableCell>
                    {
                      !isSM &&
                      <TableCell>
                        <Typography variant={'subtitle2'}>
                          {filer.accountTypeDescription}
                        </Typography>
                      </TableCell>
                    }
                    <TableCell>
                        <Stack direction={'row'} spacing={1}>
                            <Typography variant='subtitle2'>{filer.isCandidate?filer.officeSought: '-'}</Typography>
                            {
                              filer.isIncumbent && 
                              <Typography
                                variant={'caption'}
                                fontWeight={700}
                                sx={{
                                  bgcolor: alpha(theme.palette.success.light, 0.1),
                                  color: theme.palette.success.dark,
                                  paddingX: 1.5,
                                  paddingY: 0.5,
                                  borderRadius: 4,
                                  display: 'inline',
                                }}
                              >
                              {"Incumbent"}
                              </Typography>
                            }
                        </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant={'caption'}
                        fontWeight={700}
                        sx={{
                          bgcolor: filer.isActive?alpha(theme.palette.success.light, 0.1): alpha(theme.palette.error.light, 0.1),
                          color: filer.isActive? theme.palette.success.dark: theme.palette.error.dark,
                          paddingX: 1.5,
                          paddingY: 0.5,
                          borderRadius: 4,
                          display: 'inline',
                        }}
                      >
                        {filer.isActive? 'Active': 'Inactive'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                }
                {
                  !props.isFullTable &&
                  <TableRow
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 }, 
                      "&:hover": {
                      boxShadow: "0 0 10px #ccc",
                      },
                    }}
                  >
                    <TableCell size="small" 
                      sx={{
                        borderLeft: filer.cpfId===FilerListingContext.currentFilerPayload?.filer.cpfId?"2px solid": "inherit",
                        borderLeftColor: filer.cpfId===FilerListingContext.currentFilerPayload?.filer.cpfId?blue[700]: "inherit",
                        boxShadow: filer.cpfId===FilerListingContext.currentFilerPayload?.filer.cpfId?"0 0 10px #ccc": "inherit",
                      }}
                    >
                      <Typography 
                        variant='body2' 
                        className='is-clickable'
                        fontWeight={filer.cpfId===FilerListingContext.currentFilerPayload?.filer.cpfId? 550: 500}
                        onClick={() => updateCurrentFiler(filer.cpfId)}
                        color = {filer.isActive? 'primary': 'error'}    
                      >
                      {filer.cpfId} {filer.filerNameReverse}
                      </Typography>
                    </TableCell>
                  </TableRow>
                }
              </React.Fragment>
            ))}
            {
              !loadMore && searchInput.length === 0 && filerListing.length >= 198 &&
              <TableRow>
                <TableCell colSpan={props.isFullTable? 4:1} align="right">
                  <Button size="small" variant="outlined" onClick={loadMoreFilerList}>LOAD MORE</Button>
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1 }} />
      <CircularProgress
                size={50}
                className={isLoading ? "" : "hidden"}
                sx={{
                  position: "fixed",
                  left: "50%",
                  top: "60%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2
                }}
              />
    </Box>
  );
};

export default FilerListTable;
